import { FIX_SECTION } from './holdReasonFix';

/**
 * @typedef {{placeOfServiceId: string|null, treatmentServiceIds: string[]}} Delivery - what was
 *   delivered and where, in this context's words; the session and the plan store it their own way
 */

/**
 * What a held session's delivery becomes when one choice is applied to every session a plan ran.
 *
 * ONLY THE MISSING HALF IS FILLED. A session that already names a service keeps it, and one that
 * names a place keeps that: somebody may have corrected a single session by hand, and a bulk fix
 * that overwrote it would undo their work without saying so.
 *
 * @param {Delivery|null} current - null when the session records no delivery at all
 * @param {Delivery} chosen
 * @returns {Delivery}
 */
export function completeDelivery(current, chosen) {
  const currentServices = current?.treatmentServiceIds ?? [];

  return {
    placeOfServiceId: current?.placeOfServiceId ?? chosen.placeOfServiceId ?? null,
    treatmentServiceIds: currentServices.length > 0 ? currentServices : [...chosen.treatmentServiceIds],
  };
} // completeDelivery()

/** @param {Delivery|null} a @param {Delivery|null} b */
export function isSameDelivery(a, b) {
  return (a?.placeOfServiceId ?? null) === (b?.placeOfServiceId ?? null)
    && JSON.stringify(a?.treatmentServiceIds ?? []) === JSON.stringify(b?.treatmentServiceIds ?? []);
} // isSameDelivery()

/**
 * The hold a fix for this section clears. A plan's task is about one reason, so its sessions are the
 * ones held for that reason only, not the ones held for the other half.
 *
 * @param {string} section - the `FIX_SECTION` the task's fix names
 * @returns {string}
 */
export function holdReasonFixedBy(section) {
  return section === FIX_SECTION.PLACE_OF_SERVICE ? 'no_place_of_service' : 'no_treatment_service';
} // holdReasonFixedBy()

/**
 * Whether a choice fills the half the sessions are missing. A place chosen when the service is
 * missing is welcome, but without a service it fixes nothing.
 *
 * @param {string} section - the `FIX_SECTION` the task's fix names
 * @param {Delivery} chosen
 */
export function fillsMissing(section, chosen) {
  return section === FIX_SECTION.PLACE_OF_SERVICE
    ? Boolean(chosen.placeOfServiceId)
    : chosen.treatmentServiceIds.length > 0;
} // fillsMissing()
